import { appAxios } from '@/axios.js'
import { subscriptionStatuses } from '@/configs/subscriptionStatuses.js'
import store from '@/store/store.js'

const DEFAULT_TARIFF_ROUTE = 'SubscriptionTariffSelection'
const ONBOARDING_TARIFF_ROUTE = 'OnboardingTariffSelection'
const DEFAULT_PAYMENT_ROUTE = 'SubscriptionPayment'
const ONBOARDING_PAYMENT_ROUTE = 'OnboardingSubscriptionPayment'
const ONBOARDING_FINISH_ROUTE = 'Home'
/** @deprecated legacy sessionStorage key, cleared on finish */
const ONBOARDING_RECOMMENDED_PROGRAM_ID_KEY = 'OnboardingRecommendedProgramId'
const ONBOARDING_RECOMMENDED_PROGRAM_KEY_PREFIX = 'OnboardingRecommendedProgram'

function getCurrentUserId() {
  const id = store.state.user.profile?.id
  return id != null ? String(id) : null
}

function onboardingRecommendedProgramStorageKey(userId = getCurrentUserId()) {
  if (!userId) {
    return null
  }
  return `${ONBOARDING_RECOMMENDED_PROGRAM_KEY_PREFIX}:${userId}`
}

export function readSubscriptionPageSettings() {
  try {
    const raw = sessionStorage.getItem('SubscriptionPageSettings')
    if (!raw) {
      return null
    }
    return JSON.parse(raw)
  } catch {
    return null
  }
}

/**
 * @param {object} program — ответ GET /api/training-program/my/recommended/
 * @param {object} [context] — UI-контекст для тегов на экране PLAN
 */
export function saveOnboardingRecommendedProgram(program, context = {}) {
  const key = onboardingRecommendedProgramStorageKey()
  if (!key || program?.id == null) {
    return
  }

  try {
    localStorage.setItem(key, JSON.stringify({
      program,
      context: {
        place: context.place ?? null,
        targetTitle: context.targetTitle ?? null,
        placeKey: context.placeKey ?? context.place ?? null,
        frequencyCount: context.frequencyCount ?? null,
        frequencyTitle: context.frequencyTitle ?? null,
        experienceCode: context.experienceCode ?? null,
      },
    }))
  } catch {
    // Quota / private mode — не блокируем онбординг
  }
}

export function readOnboardingRecommendedProgram() {
  const key = onboardingRecommendedProgramStorageKey()
  if (!key) {
    return null
  }

  try {
    const raw = localStorage.getItem(key)
    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw)
    if (parsed?.program?.id == null) {
      return null
    }

    return {
      program: parsed.program,
      context: parsed.context || {},
    }
  } catch {
    return null
  }
}

export function readOnboardingRecommendedProgramId(
  settings = readSubscriptionPageSettings(),
) {
  const fromSettings = settings?.trainingProgramId
  if (fromSettings != null && fromSettings !== '') {
    return Number(fromSettings)
  }

  const cached = readOnboardingRecommendedProgram()
  if (cached?.program?.id != null) {
    const id = Number(cached.program.id)
    return Number.isFinite(id) ? id : null
  }

  return null
}

export function clearOnboardingRecommendedProgram() {
  const key = onboardingRecommendedProgramStorageKey()
  if (key) {
    localStorage.removeItem(key)
  }
  sessionStorage.removeItem(ONBOARDING_RECOMMENDED_PROGRAM_ID_KEY)
}

export function resolveTariffSelectionRouteName(settings = readSubscriptionPageSettings()) {
  if (settings?.tariffRouteName) {
    return settings.tariffRouteName
  }

  if (settings?.fromOnboarding) {
    return ONBOARDING_TARIFF_ROUTE
  }

  return DEFAULT_TARIFF_ROUTE
}

export function resolvePaymentRouteName(settings = readSubscriptionPageSettings()) {
  if (settings?.fromOnboarding || settings?.tariffRouteName === ONBOARDING_TARIFF_ROUTE) {
    return ONBOARDING_PAYMENT_ROUTE
  }

  return DEFAULT_PAYMENT_ROUTE
}

export function hasActiveSubscription(subscriptions = store.state.user.profile?.subscriptions) {
  return (subscriptions ?? []).some(sub => sub.status === subscriptionStatuses.active)
}

/** Все шаги анкеты онбординга уже finished_at */
export function areOnboardingStepsFinished(
  profile = store.state.user.profile,
  directories = store.state.user.directories,
) {
  const totalSteps = directories?.onboarding_step_list?.length ?? 0
  if (!totalSteps) {
    return false
  }

  let maxFinishedStep = 0
  for (const userStep of profile?.onboarding ?? []) {
    if (userStep.finished_at) {
      maxFinishedStep = Math.max(maxFinishedStep, userStep.step)
    }
  }

  return maxFinishedStep >= totalSteps
}

export function isOnboardingSubscriptionFlow(settings = readSubscriptionPageSettings()) {
  // Только явный fromOnboarding — иначе после logout/нового юзера
  // в sessionStorage остаются tariffRouteName/wizardReturnName и гейт шлёт на тариф.
  return Boolean(settings?.fromOnboarding)
}

/**
 * Закрывает незакрытые шаги онбординга через API —
 * бэк после последнего PUT сам ставит onboarding_finished.
 */
export async function finishRemainingOnboardingSteps() {
  const totalSteps = store.state.user.directories?.onboarding_step_list?.length ?? 0
  if (!totalSteps) {
    return
  }

  let steps = [...(store.state.user.profile?.onboarding ?? [])]

  for (let stepIndex = 1; stepIndex <= totalSteps; stepIndex += 1) {
    let userStep = steps.find(item => item.step === stepIndex)

    if (userStep?.finished_at) {
      continue
    }

    if (!userStep) {
      const res = await appAxios.onboarding.start(stepIndex)
      userStep = res.data
      steps = [...steps.filter(item => item.step !== stepIndex), userStep]
      store.commit('user/setProfile', { onboarding: steps })
    }

    await appAxios.onboarding.finish(userStep.id)
  }
}

function clearOnboardingSubscriptionFlowFlag() {
  sessionStorage.removeItem('SubscriptionPageSettings')
  clearOnboardingRecommendedProgram()
}

/**
 * После оплаты: POST /api/training-program/my/ с training_program_id из recommended.
 */
async function createOnboardingRecommendedProgram() {
  const profile = store.state.user.profile
  if ((profile?.training_programs ?? []).length > 0) {
    return
  }

  const trainingProgramId = readOnboardingRecommendedProgramId()
  if (!trainingProgramId) {
    return
  }

  try {
    await store.dispatch('user/createTrainingProgram', {
      training_program_id: trainingProgramId,
    })
  } catch {
    // Не блокируем снятие онбординг-флага: иначе пользователь застрянет на оплате.
  }
}

let finishOnboardingInFlight = null

/**
 * Bodybase: держим онбординг до первой active-подписки.
 * Create recommended-программы → закрываем шаги (onboarding_finished) → Home.
 * @returns {Promise<boolean>}
 */
export async function tryFinishBodybaseOnboardingOnActiveSubscription() {
  if (finishOnboardingInFlight) {
    return finishOnboardingInFlight
  }

  finishOnboardingInFlight = (async () => {
    if (!store.getters['app/isBodybaseProject']) {
      return false
    }

    const profile = store.state.user.profile
    if (!profile) {
      return false
    }

    if (!hasActiveSubscription(profile.subscriptions)) {
      return false
    }

    const inOnboardingFlow = isOnboardingSubscriptionFlow()
    if (profile.onboarding_finished && !inOnboardingFlow) {
      return false
    }

    if (!profile.onboarding_finished) {
      await createOnboardingRecommendedProgram()
      await finishRemainingOnboardingSteps()
      await store.dispatch('user/fetchProfile')
    }

    store.commit('app/setHideNavBar', true)
    clearOnboardingSubscriptionFlowFlag()

    return true
  })()

  try {
    return await finishOnboardingInFlight
  } finally {
    finishOnboardingInFlight = null
  }
}

/** @deprecated используй tryFinishBodybaseOnboardingOnActiveSubscription */
export function finishOnboardingAfterPayment() {
  return tryFinishBodybaseOnboardingOnActiveSubscription()
}

export function getOnboardingFinishRouteName() {
  return ONBOARDING_FINISH_ROUTE
}
